import { StepId } from "./StepDefinitions";

export type PartialDogForm = {
  name?: string;
  breed?: string;

  years?: number | string;
  months?: number | string;

  sex?: string;
  reproduction?: string;

  weightKg?: number | string;
  bodyCondition?: string;
  activityLevel?: string;

  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;

  allergies?: string[];
  healthIssues?: string[];
  treatFrequency?: string;
  selectedPackSize?: string | number;
  selectedPlanDays?: number | string;

  [key: string]: unknown;
};

export type StepValidationResult =
  | { valid: true }
  | { valid: false; message: string; field: string };

function isBlankString(v: unknown): boolean {
  return typeof v === "string" ? v.trim().length === 0 : false;
}

function requireString(value: unknown, message: string, field: string): StepValidationResult {
  if (typeof value !== "string" || value.trim().length === 0) {
    return { valid: false, message, field };
  }
  return { valid: true };
}

function parseNonNegativeNumber(
  input: number | string | "" | undefined | null
): number {
  if (input === "" || input == null) return 0;
  const n = Number(input);
  if (Number.isNaN(n) || n < 0) return NaN;
  return n;
}

function isPlausibleEmail(email: string): boolean {
  // intentionally simple; good enough for quiz validation
  const trimmed = email.trim();
  if (trimmed.length < 5) return false;
  const basic = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return basic.test(trimmed);
}

function isPlausiblePhone(phone: string): boolean {
  const trimmed = phone.trim();
  if (trimmed.length === 0) return false;

  // Allow digits, spaces, +, -, (), and dots
  const allowed = /^[0-9+\-\s().]+$/;
  if (!allowed.test(trimmed)) return false;

  // Count digits only
  const digits = trimmed.replace(/\D/g, "");
  // Rough ITU-style range: 7–15 digits
  if (digits.length < 7 || digits.length > 15) return false;

  return true;
}

export function validateStep(
  step: StepId,
  form: PartialDogForm | undefined | null
): StepValidationResult {
  if (!form) {
    if (step === "basic") {
      return {
        valid: false,
        message: "We'd love to get to know your pup! Let's start with their name.",
        field: "name",
      };
    }

    return { valid: true };
  }

  const dogName =
    form.name && !isBlankString(form.name) ? form.name.trim() : "your pup";

  switch (step) {
    case "basic": {
      // Name
      {
        const res = requireString(form.name, "What's your furry friend's name?", "name");
        if (!res.valid) return res;
      }

      // Breed
      {
        const res = requireString(
          form.breed,
          `We'd love to know ${dogName}'s breed!`,
          "breed"
        );
        if (!res.valid) return res;
      }

      // Age
      const years = parseNonNegativeNumber(form.years as any);
      const months = parseNonNegativeNumber(form.months as any);

      if (Number.isNaN(years) || Number.isNaN(months)) {
        return {
          valid: false,
          message: `Hmm, that doesn't look quite right. How old is ${dogName}?`,
          field: "age",
        };
      }

      const totalMonths = years * 12 + months;

      if (totalMonths === 0) {
        return {
          valid: false,
          message: `How old is ${dogName}? We need this to create the perfect plan.`,
          field: "age",
        };
      }

      if (totalMonths > 20 * 12) {
        return {
          valid: false,
          message: `Wow, ${dogName} must be wise! But we need an age under 20 years.`,
          field: "age",
        };
      }

      {
        const res = requireString(
          form.sex,
          `Is ${dogName} a handsome boy or a beautiful girl?`,
          "sex"
        );
        if (!res.valid) return res;
      }

      // Reproduction
      {
        const res = requireString(
          form.reproduction,
          `Has ${dogName} been neutered or spayed?`,
          "reproduction"
        );
        if (!res.valid) return res;
      }

      return { valid: true };
    }

    case "about": {
      const weight = parseNonNegativeNumber(form.weightKg as any);

      if (Number.isNaN(weight) || weight <= 0) {
        return {
          valid: false,
          message: `How much does ${dogName} weigh? This helps us get portions just right.`,
          field: "weight",
        };
      }

      // You *could* add a soft upper cap here too (e.g. 120kg) if you want.

      {
        const res = requireString(
          form.bodyCondition,
          `What's ${dogName}'s body shape? This helps us tailor their meals.`,
          "bodyCondition"
        );
        if (!res.valid) return res;
      }

      {
        const res = requireString(
          form.activityLevel,
          `How active is ${dogName}? Couch potato or adventure buddy?`,
          "activityLevel"
        );
        if (!res.valid) return res;
      }

      // Treat frequency lives on the last "about" sub-step since the redesign,
      // so it must be validated here — not on the "treat" (allergies) step.
      {
        const res = requireString(
          form.treatFrequency,
          `How often does ${dogName} get treats? We won't judge!`,
          "treatFrequency"
        );
        if (!res.valid) return res;
      }

      return { valid: true };
    }

    case "treat": {
      // The treat step carries the whole end-of-MY-DOG cluster (allergies,
      // health, current food, tried-fresh, fussiness, goals). Every question
      // must be answered before the step can complete — otherwise clicking
      // Next on an unanswered sub-step used to fall through this validation
      // and jump straight to email capture, skipping the remaining questions.
      if (!Array.isArray(form.allergies) || form.allergies.length === 0) {
        return {
          valid: false,
          message: `Does ${dogName} have any allergies? Pick "No allergies" if not.`,
          field: "allergies",
        };
      }

      if (!Array.isArray(form.healthIssues) || form.healthIssues.length === 0) {
        return {
          valid: false,
          message: `Any health issues we should know about? Pick "No health issues" if not.`,
          field: "healthIssues",
        };
      }

      const currentFood = Array.isArray(form.currentFood) ? form.currentFood : [];
      if (currentFood.length === 0) {
        return {
          valid: false,
          message: `What does ${dogName} eat at the moment?`,
          field: "currentFood",
        };
      }

      // Tried-fresh only applies when the dog isn't already on fresh food.
      if (!currentFood.includes("FRESH")) {
        const res = requireString(
          form.triedFresh as string,
          `Has ${dogName} tried fresh food before?`,
          "triedFresh"
        );
        if (!res.valid) return res;
      }

      {
        const res = requireString(
          form.fussiness as string,
          `How is ${dogName} at mealtimes?`,
          "fussiness"
        );
        if (!res.valid) return res;
      }

      {
        const res = requireString(
          form.feedingGoal as string,
          `What are you most hoping fresh food will do for ${dogName}?`,
          "feedingGoal"
        );
        if (!res.valid) return res;
      }

      return { valid: true };
    }

    case "owner": {
      {
        const res = requireString(form.ownerName, "We'd love to know your name!", "ownerName");
        if (!res.valid) return res;
      }

      if (!form.ownerEmail || isBlankString(form.ownerEmail)) {
        return {
          valid: false,
          message: "Where should we send your personalised plan?",
          field: "ownerEmail",
        };
      }

      if (!isPlausibleEmail(form.ownerEmail)) {
        return {
          valid: false,
          message: "That email doesn't look quite right. Mind double-checking?",
          field: "ownerEmail",
        };
      }

      // Phone is optional, but if present it should look like a real number
      if (form.ownerPhone && !isBlankString(form.ownerPhone)) {
        if (!isPlausiblePhone(form.ownerPhone)) {
          return {
            valid: false,
            message: "That phone number doesn't look quite right. Try including the country code.",
            field: "ownerPhone",
          };
        }
      }

      return { valid: true };
    }

    default:
      // For any unknown step, don't block navigation
      return { valid: true };
  }
}
